/**
 * Carry a workspace id from an app link onto the cart (#1451).
 *
 * `shopifyWebhookNode` attributes an order two ways, and only the first is the
 * contract: the `ruck_organization_id` CART ATTRIBUTE, then, failing that, the
 * buyer's email matched against a workspace owner. The fallback is genuinely
 * weak — someone who checks out under a personal address matches nothing, the
 * webhook answers 200 on `unattributed` so Shopify never retries, and the sale
 * disappears into the pile #1451 is about. They paid and nothing provisions.
 *
 * Carts the APP builds already set the attribute, because `createStorefrontCart`
 * puts it there. A buyer who taps "add people on the storefront" in the mobile
 * app and then composes their own cart here has no such thing — the app never
 * saw the cart. So the link carries the id as a query parameter and this writes
 * it onto whatever cart they end up with.
 *
 * Two details that matter:
 *
 * - It writes on EVERY page, not just the product page, because the parameter
 *   arrives on the landing page and the cart may not exist until later. Writing
 *   an attribute to an empty cart is legal and it persists.
 * - It remembers the id for the session, so the attribute survives the buyer
 *   browsing away from the link before adding anything.
 *
 * The id is a workspace identifier, not a credential. It travels on every cart
 * the app's own checkout builds, and holding one grants nothing: the webhook
 * only uses it to decide which workspace a PAID order belongs to.
 *
 * The key is duplicated from `SHOPIFY_ORG_ATTRIBUTE_KEY` in `convex/_lib/env.ts`
 * and `storefront/pricing.test.ts` fails if the two drift.
 */
(function () {
  var KEY = 'ruck_organization_id';
  var STORE_KEY = 'ruck-org-attribution';

  function remembered() {
    try {
      return sessionStorage.getItem(STORE_KEY);
    } catch {
      return null;
    }
  }

  function remember(value) {
    try {
      sessionStorage.setItem(STORE_KEY, value);
    } catch {
      // Private mode. The attribute still gets written for this page load.
    }
  }

  function run() {
    var fromUrl = null;
    try {
      fromUrl = new URLSearchParams(window.location.search).get(KEY);
    } catch {
      fromUrl = null;
    }

    // A Convex id is short and alphanumeric. Anything else is not one of ours,
    // and refusing it keeps arbitrary querystring content off the order.
    if (fromUrl !== null && !/^[a-z0-9]{16,64}$/i.test(fromUrl)) fromUrl = null;

    var orgId = fromUrl || remembered();
    if (!orgId) return;
    if (fromUrl) remember(fromUrl);

    var attributes = {};
    attributes[KEY] = orgId;

    fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ attributes: attributes }),
    }).catch(function (err) {
      // Never block a purchase over attribution. The email fallback still
      // exists, and an unattributed order is recoverable with `pnpm
      // shopify:orders` — a failed page is not.
      console.warn('[ruck] could not attach workspace attribution', err);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', run);
  } else {
    run();
  }
})();
